
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Search, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";
import { useAuth } from "@/components/AuthProvider";

export const ProgressList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslations("progress");
  const { getPath } = useLanguagePath();
  const { user } = useAuth();

  const { data: students, isLoading } = useQuery({
    // Include user ID in the query key to isolate data between users
    queryKey: ["students-with-progress", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        console.error("No authenticated user found");
        return [];
      }

      const { data, error } = await supabase
        .from("students")
        .select(`
          *,
          physical_assessments (
            weight,
            body_fat_percentage,
            assessment_date
          ),
          training_sessions (
            created_at
          )
        `)
        .eq("trainer_id", user.id) // Ensure we only get students for this trainer
        .eq("status", "active")
        .order("first_name", { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    staleTime: 60000 // 1 minute
  });

  const calculateProgress = (student: any) => {
    if (!student.physical_assessments?.length) return null;

    const sortedAssessments = [...student.physical_assessments].sort(
      (a, b) => new Date(b.assessment_date).getTime() - new Date(a.assessment_date).getTime()
    );

    const lastAssessment = sortedAssessments[0];
    const previousAssessment = sortedAssessments[1];

    if (!previousAssessment) return null;

    const weightChange = lastAssessment.weight && previousAssessment.weight
      ? (lastAssessment.weight - previousAssessment.weight).toFixed(1)
      : null;

    return {
      weightChange,
      sessionsCount: student.training_sessions?.length || 0,
    };
  };

  const filteredStudents = students?.filter((student) =>
    `${student.first_name} ${student.last_name}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold tracking-tight gradient-text dark:text-white">{t("title")}</h2>
          <p className="text-muted-foreground dark:text-gray-300">
            {t("subtitle")}
          </p>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80 dark:text-flame dark:hover:text-flame/80">
                  {t("breadcrumbs.dashboard")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">{t("breadcrumbs.progress")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <div className="relative flex-1 w-full sm:max-w-xs">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground dark:text-gray-400" />
        <Input
          placeholder={t("search")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 bg-white/80 border border-slate-100 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        />
      </div>

      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("table.student")}</TableHead>
              <TableHead>{t("table.weightChange")}</TableHead>
              <TableHead>{t("table.sessions")}</TableHead>
              <TableHead className="text-right">{t("table.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center dark:text-gray-300">
                  {t("loading")}
                </TableCell>
              </TableRow>
            ) : filteredStudents?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center dark:text-gray-300">
                  {t("noStudents")}
                </TableCell>
              </TableRow>
            ) : (
              filteredStudents?.map((student) => {
                const progress = calculateProgress(student);
                return (
                  <TableRow key={student.id} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/50">
                    <TableCell className="font-medium dark:text-white">
                      {student.first_name} {student.last_name}
                    </TableCell>
                    <TableCell>
                      {progress?.weightChange 
                        ? <span className={Number(progress.weightChange) < 0 ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"}>
                            {progress.weightChange} kg
                          </span> 
                        : <span className="dark:text-gray-300">{t("noData")}</span>}
                    </TableCell>
                    <TableCell className="dark:text-white">{progress?.sessionsCount || 0}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(getPath(`/dashboard/progress/${student.id}`))}
                        className="hover:bg-flame/10 hover:text-flame dark:text-white dark:hover:text-flame"
                      >
                        <TrendingUp className="h-4 w-4 mr-2 text-flame" />
                        {t("viewProgress")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
